"use client"
import {useState, useEffect, useRef} from "react"
import { useNavigate } from "react-router-dom"
import Layout from "../../components/Layout"
import { toast } from "react-toastify"
import { showSuccess } from "../../utils/toastUtils"
import { dispatchCartEvent, CART_EVENTS } from "../../utils/cartEvents"
import { getBrowserId } from "../../utils/browserUtils"



const UploadPage = () => {
    const [isMobile, setIsMobile] = useState(false)
    const [isTablet, setIsTablet] = useState(false)
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
    const navigate = useNavigate()
    const [showPrayerModal, setShowPrayerModal] = useState(false)

    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false); // Changed to modal
    const [isUploading, setIsUploading] = useState(false);
    const [formSubmissions, setFormSubmissions] = useState({});
    const [filesAddedToCart, setFilesAddedToCart] = useState(new Set());
    const [processingFileId, setProcessingFileId] = useState(null);
    const fileInputRef = useRef(null);

    const [cartCount, setCartCount] = useState(0);
    const [forms, setForms] = useState([]);
    const [isLoadingForms, setIsLoadingForms] = useState(true);

    // New states for checkbox selection
    const [selectedFileIds, setSelectedFileIds] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [showBulkAddModal, setShowBulkAddModal] = useState(false);
    const [bulkAddOption, setBulkAddOption] = useState(null); // 'all' or 'selected'

    const CloudUploadIcon = () => (
        <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ color: "#7c3aed" }}
        >
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            <path d="M12,11L16,15H13V19H11V15H8L12,11Z" />
        </svg>
    );

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const files = Array.from(e.dataTransfer.files);
            const validFiles = files.filter(
                (file) =>
                    file.type === "application/pdf" ||
                    file.type.startsWith("image/jpeg") ||
                    file.type.startsWith("image/jpg")
            );
            setSelectedFiles((prev) => [...prev, ...validFiles]);
        }
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            const validFiles = files.filter(
                (file) =>
                    file.type === "application/pdf" ||
                    file.type.startsWith("image/jpeg") ||
                    file.type.startsWith("image/jpg")
            );

            if (validFiles.length === 0) {
                toast.error("Please select PDF or JPG/JPEG files only.");
                return;
            }

            setSelectedFiles((prev) => [...prev, ...validFiles]);

            // Reset the file input to ensure it works for future selections
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleSelectClick = () => {
        // Reset the file input value before clicking to ensure it works every time
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    // Handle Add to Cart button click
    const handleAddToCartClick = () => {
        // Prevent multiple clicks if already processing
        if (processingFileId === 'bulk') {
            return;
        }

        // If only one file, add directly
        if (uploadedFiles.length === 1) {
            handleAddToBasket(uploadedFiles[0]);
            return;
        }

        // For multiple files, show confirmation modal
        setShowBulkAddModal(true);
    };

    // Fetch uploaded files
    const fetchUploadedFiles = async () => {
        try {
            const browserId = getBrowserId();
            const token = localStorage.getItem("temple_token") || localStorage.getItem("jwtToken");
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const response = await fetch(`${API_URL}/uploads/files?browser_id=${browserId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-browser-id': browserId,
                },
            });
            const data = await response.json();
            console.log('Fetched uploaded files:', data);
            if (data.success) {
                // Backend should already filter out 'in_cart' files (status='uploaded' only)
                // Also filter out files that have already been added to cart locally
                const filteredFiles = data.data.filter(file => !filesAddedToCart.has(file.id));
                console.log('Files after filtering:', filteredFiles.length, 'filesAddedToCart:', Array.from(filesAddedToCart));
                setUploadedFiles(filteredFiles);
            }
        } catch (error) {
            console.error("Error fetching files:", error);
        }
    };

    // Upload files to backend
    const handleUploadFiles = async () => {
        if (selectedFiles.length === 0) {
            toast.error("Please select files first.");
            return;
        }
        setIsUploading(true);
        try {
            const browserId = getBrowserId();
            const token = localStorage.getItem("jwtToken");
            const formData = new FormData();
            selectedFiles.forEach((file) => formData.append("files", file));
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const response = await fetch(`${API_URL}/uploads/upload`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-browser-id': browserId,
                },
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                showSuccess(`${data.data.length} file(s) uploaded successfully!`);
                setSelectedFiles([]);
                setShowUploadModal(false); // Close modal after successful upload
                fetchUploadedFiles();
            } else {
                toast.error(data.message || "Upload failed. Please try again.");
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Upload failed. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    // Handle form submission changes
    const handleFormSubmissionChange = (fileId, field, value) => {
        const updates = { [field]: value };

        // If changing requestType (form selection), also store form_orientation and report_id
        if (field === 'requestType' && value) {
            const selectedForm = forms.find(f => f.formType === value);
            if (selectedForm) {
                updates.form_orientation = selectedForm.orientation || 'portrait';
                updates.report_id = selectedForm.report_id || null;
                console.log('Selected form:', value, 'orientation:', updates.form_orientation, 'report_id:', updates.report_id);
            }
        }

        setFormSubmissions((prev) => ({
            ...prev,
            [fileId]: {
                ...prev[fileId],
                ...updates,
            },
        }));
    };

    // Handle individual file checkbox selection
    const handleFileSelect = (fileId, checked) => {
        const newSelection = new Set(selectedFileIds);
        if (checked) {
            newSelection.add(fileId);
        } else {
            newSelection.delete(fileId);
        }
        setSelectedFileIds(newSelection);

        // Update selectAll state based on whether all files are selected
        setSelectAll(newSelection.size === uploadedFiles.length && uploadedFiles.length > 0);
    };

    // Handle Select All checkbox
    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            setSelectedFileIds(new Set(uploadedFiles.map(file => file.id)));
        } else {
            setSelectedFileIds(new Set());
        }
    };

    // Validate if all required fields are filled for a file
    const validateFileData = (file) => {
        const submission = formSubmissions[file.id];
        return submission &&
               submission.requestType &&
               submission.amount &&
               parseFloat(submission.amount) > 0;
    };

    // Handle bulk add to basket
    const handleBulkAddToBasket = async () => {
        // For single file, use the existing behavior
        if (uploadedFiles.length === 1) {
            await handleAddToBasket(uploadedFiles[0]);
            return;
        }

        // For multiple files, check validation
        const filesToAdd = bulkAddOption === 'all'
            ? uploadedFiles
            : uploadedFiles.filter(f => selectedFileIds.has(f.id));

        // Validate all files have required data
        const invalidFiles = filesToAdd.filter(file => !validateFileData(file));
        if (invalidFiles.length > 0) {
            // More detailed error message
            const missingDetails = invalidFiles.map((file, index) => {
                const submission = formSubmissions[file.id] || {};
                const missing = [];
                if (!submission.requestType) missing.push('Request Type');
                if (!submission.amount || parseFloat(submission.amount) <= 0) missing.push('Amount');
                return `File ${uploadedFiles.findIndex(f => f.id === file.id) + 1}: Missing ${missing.join(', ')}`;
            });
            toast.error(
                <div>
                    <strong>Please fill in all required fields:</strong>
                    <ul style={{ marginTop: '5px', marginBottom: 0, paddingLeft: '20px' }}>
                        {missingDetails.map((detail, i) => <li key={i}>{detail}</li>)}
                    </ul>
                </div>,
                { autoClose: 5000 }
            );
            setShowBulkAddModal(false);
            return;
        }

        // Process all files
        setProcessingFileId('bulk');

        try {
            const token = localStorage.getItem("temple_token") || localStorage.getItem("jwtToken");
            const browserId = getBrowserId();

            let successCount = 0;
            let failCount = 0;

            for (const file of filesToAdd) {
                const submission = formSubmissions[file.id];
                try {
                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const response = await fetch(`${API_URL}/uploads/add-to-basket`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`,
                            "x-browser-id": browserId,
                        },
                        body: JSON.stringify({
                            fileId: file.id,
                            requestType: submission.requestType,
                            amount: parseFloat(submission.amount),
                            remarks: submission.remarks || "",
                            browser_id: browserId,
                            form_orientation: submission.form_orientation || 'portrait',
                            report_id: submission.report_id || null,
                        }),
                    });

                    const data = await response.json();
                    if (data.success) {
                        successCount++;
                        setFilesAddedToCart((prev) => new Set([...prev, file.id]));
                        setUploadedFiles((prev) => prev.filter((f) => f.id !== file.id));
                    } else {
                        failCount++;
                    }
                } catch (error) {
                    console.error("Error adding file to basket:", error);
                    failCount++;
                }
            }

            if (successCount > 0) {
                showSuccess(`${successCount} item(s) added to cart successfully!`);

                // Clear selections and close modal
                setSelectedFileIds(new Set());
                setSelectAll(false);
                setShowBulkAddModal(false);
                setBulkAddOption(null);

                // Clear form submissions for added files
                setFormSubmissions((prev) => {
                    const newSubmissions = { ...prev };
                    filesToAdd.forEach(file => {
                        delete newSubmissions[file.id];
                    });
                    return newSubmissions;
                });

                // Dispatch cart events
                window.dispatchEvent(new Event('cartUpdated'));
                dispatchCartEvent(CART_EVENTS.CART_UPDATED, {});

                await fetchCartCount();
                await fetchUploadedFiles();

                // Redirect to basket after bulk add
                setTimeout(() => {
                    navigate('/basket');
                }, 500);
            }

            if (failCount > 0) {
                toast.error(`Failed to add ${failCount} item(s) to cart`);
            }
        } catch (error) {
            console.error("Bulk add error:", error);
            toast.error("Failed to add items to basket");
        } finally {
            setProcessingFileId(null);
        }
    };

    // Modified single add to basket for consistency
    const handleAddToBasket = async (file) => {
        const submission = formSubmissions[file.id];
        console.log('Add to basket clicked for file:', file.id, 'submission:', submission);
        
        if (!submission || !submission.requestType || !submission.amount || parseFloat(submission.amount) <= 0) {
            toast.error("Please fill in all required fields with valid values");
            return;
        }
        
        // Set processing state to disable button
        setProcessingFileId(file.id);
        
        try {
            const token = localStorage.getItem("temple_token") || localStorage.getItem("jwtToken");
            const browserId = getBrowserId();
            
            console.log('Sending add-to-basket request with browser_id:', browserId);
            
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const response = await fetch(`${API_URL}/uploads/add-to-basket`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "x-browser-id": browserId,
                },
                body: JSON.stringify({
                    fileId: file.id,
                    requestType: submission.requestType,
                    amount: parseFloat(submission.amount),
                    remarks: submission.remarks || "",
                    browser_id: browserId,
                    form_orientation: submission.form_orientation || 'portrait',
                    report_id: submission.report_id || null,
                }),
            });
            
            console.log('Response status:', response.status);
            const data = await response.json();
            console.log('Response data:', data);
            
            if (data.success) {
                showSuccess("Item added to cart successfully!");

                // Redirect to basket page
                setTimeout(() => {
                    navigate('/basket');
                }, 500);

                // Add file ID to the set of files added to cart
                setFilesAddedToCart((prev) => new Set([...prev, file.id]));
                
                // Remove the file from uploadedFiles list immediately (optimistic update)
                setUploadedFiles((prev) => {
                    const filtered = prev.filter((f) => f.id !== file.id);
                    console.log('Filtered files, removed file:', file.id, 'remaining:', filtered.length);
                    return filtered;
                });
                
                // Clear the form submission data for this file
                setFormSubmissions((prev) => {
                    const newSubmissions = { ...prev };
                    delete newSubmissions[file.id];
                    return newSubmissions;
                });

                // Dispatch legacy cartUpdated event for backward compatibility
                console.log('Dispatching cartUpdated event');
                window.dispatchEvent(new Event('cartUpdated'));
                
                // Also dispatch new cart event system
                dispatchCartEvent(CART_EVENTS.ITEM_ADDED, {
                    requestType: submission.requestType,
                    amount: parseFloat(submission.amount)
                });
                
                // Update cart count
                console.log('Fetching updated cart count...');
                await fetchCartCount();
                
                // Dispatch CART_UPDATED event
                dispatchCartEvent(CART_EVENTS.CART_UPDATED, {});

                // Refresh the uploaded files list from backend to get updated status
                console.log('Refreshing uploaded files from backend...');
                await fetchUploadedFiles();
                
                console.log('Successfully added to cart and refreshed');
            } else {
                console.error('Add to basket failed:', data.message);
                toast.error(data.message || "Failed to add to basket");
            }
        } catch (error) {
            console.error("Add to basket error:", error);
            toast.error("Failed to add to basket: " + error.message);
        } finally {
            // Clear processing state
            setProcessingFileId(null);
        }
    };

    // View uploaded file
    const handleViewFile = async (fileId) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const response = await fetch(`${API_URL}/uploads/files/${fileId}/view`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.ok) {
                // Backend now returns binary data (blob), not JSON
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                window.open(url, "_blank");
                // Clean up the object URL after a short delay
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            } else {
                toast.error("Failed to load file");
            }
        } catch (error) {
            console.error("Error viewing file:", error);
            toast.error("Failed to load file");
        }
    };

    // Delete uploaded file
    const handleDeleteFile = async (fileId) => {
        if (!window.confirm("Are you sure you want to delete this file?")) {
            return;
        }
        try {
            const browserId = getBrowserId();
            const token = localStorage.getItem("jwtToken");
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const response = await fetch(`${API_URL}/uploads/files/${fileId}?browser_id=${browserId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-browser-id': browserId,
                },
            });
            const data = await response.json();
            if (data.success) {
                showSuccess("File deleted successfully");
                fetchUploadedFiles();
            } else {
                toast.error(data.message || "Failed to delete file");
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Failed to delete file");
        }
    };

    // Open upload modal
    const handleOpenUploadModal = () => {
        setSelectedFiles([]); // Clear any previously selected files
        setShowUploadModal(true);
    };

    const fetchCartCount = async () => {
        try {
            const browserId = getBrowserId();
            const token = localStorage.getItem("temple_token") || localStorage.getItem("jwtToken");
            if (!token) return;
            
            // Use the proper basket count endpoint with browser_id
            const getApiUrl = () => {
                if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
                const hostname = window.location.hostname;
                if (hostname === '18.133.181.177' || hostname.includes('18.133.181.177')) {
                    return 'http://18.133.181.177:3001/api';
                }
                return 'http://localhost:3001/api';
            };
            const API_URL = getApiUrl();
            
            const response = await fetch(`${API_URL}/offerings/basket/count?browser_id=${browserId}`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "x-browser-id": browserId,
                    "Content-Type": "application/json",
                },
            });
            console.log('Cart count response status:', response.status);
            if (response.ok) {
                const data = await response.json();
                console.log('Cart count data:', data);
                if (data.success && data.data && typeof data.data.item_count === "number") {
                    console.log('Setting cart count to:', data.data.item_count);
                    setCartCount(data.data.item_count);
                } else {
                    console.log('Invalid cart count response format');
                }
            }
        } catch (error) {
            console.error("Error fetching cart count:", error);
        }
    };

    // Load uploaded files on component mount
    // Fetch forms on mount
    const fetchForms = async () => {
        try {
            setIsLoadingForms(true);
            const API_URL = process.env.REACT_APP_API_URL || '/api';
            const response = await fetch(`${API_URL}/forms/public`);
            const data = await response.json();

            if (data.success) {
                console.log('Fetched forms:', data.data);
                setForms(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching forms:', error);
            toast.error('Failed to load forms');
        } finally {
            setIsLoadingForms(false);
        }
    };

    useEffect(() => {
        fetchForms();
        fetchUploadedFiles();
        fetchCartCount();
        const handleCartUpdate = () => {
            fetchCartCount();
        };
        window.addEventListener("cartUpdated", handleCartUpdate);
        return () => {
            window.removeEventListener("cartUpdated", handleCartUpdate);
        };
    }, []);

    // Dashboard cards data

    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth
            setIsMobile(width <= 768)
            setIsTablet(width > 768 && width <= 1024)
        }

        checkScreenSize()
        window.addEventListener("resize", checkScreenSize)

        return () => {
            window.removeEventListener("resize", checkScreenSize)
        }
    }, [navigate])

    const breadcrumb = (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "0.9rem" }}>
            <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/home")}
            >
                <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
            </svg>
            <span>/</span>
            <span style={{ cursor: "pointer" }} onClick={() => navigate("/home")}>Home</span>
            <span>/</span>
            <span>Upload Prayer Form</span>
        </div>
    )

    const getSidebarWidth = () => {
        if (isMobile) return "80%"
        return sidebarCollapsed ? "80px" : "250px"
    }

    const getMainContentMargin = () => {
        if (isMobile) return "0"
        return sidebarCollapsed ? "80px" : "250px"
    }

    const getMainContentWidth = () => {
        if (isMobile) return "100%"
        return sidebarCollapsed ? "calc(100% - 80px)" : "calc(100% - 250px)"
    }

    const styles = {
        mainContent: {
            marginLeft: getMainContentMargin(),
            paddingTop: "80px",
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            width: getMainContentWidth(),
            transition: "margin-left 0.3s ease, width 0.3s ease",
        },
        breadcrumb: {
            display: "flex",
            alignItems: "center",
            marginBottom: "30px",
            color: "#6b7280",
            fontSize: "0.9rem",
            gap: "8px",
        },
        cardsGrid: {
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: "20px",
            width: "100%",
            marginTop: "30px",
        },
        card: {
            backgroundColor: "#7fb3a3",
            borderRadius: "12px",
            padding: "10px",
            textAlign: "center",
            color: "white",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            width: "90%",
            margin: "0 auto",
            marginTop: "20px",
            transition: "all 0.3s ease",
        },
        cardIcon: {
            fontSize: "3rem",
            marginBottom: "20px",
            opacity: 0.9,
            display: "flex",
            justifyContent: "center",
        },
        cardTitle: {
            fontSize: "1.3rem",
            fontWeight: "600",
            marginBottom: "15px",
            lineHeight: "1.3",
        },
        cardDescription: {
            fontSize: "0.95rem",
            opacity: 0.9,
            lineHeight: "1.4",
        },
        error: {
            color: "#ef4444",
            fontSize: "0.9rem",
            textAlign: "center",
            marginBottom: "20px",
        },

        uploadSection: {
            margin: "0 auto",
            width: "100%",
        },
        uploadTitle: {
            color: "#374151",
            textAlign: "center",
            fontSize: "1.5rem",
        },
        uploadArea: {
            borderRadius: "12px",
            padding: "60px 40px",
            textAlign: "center",
            backgroundColor: dragActive ? "#f3f4f6" : "#fafbfc",
            transition: "all 0.3s ease",
            cursor: "pointer",
            position: "relative",
            marginTop: "40px",
        },
        uploadText: {
            fontSize: "1.1rem",
            color: "#374151",
            marginBottom: "25px",
            fontWeight: "500",
        },
        selectButton: {
            backgroundColor: "#6b7280",
            color: "white",
            border: "none",
            padding: "12px 30px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "1rem",
            fontWeight: "600",
            marginBottom: "20px",
            transition: "background-color 0.2s",
        },
        fileRestriction: {
            color: "#ef4444",
            fontSize: "0.9rem",
            fontStyle: "italic",
        },
        hiddenInput: {
            display: "none",
        },
        selectedFiles: {
            marginTop: "20px",
            padding: "20px",
            backgroundColor: "white",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
        },
        fileItem: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 0",
            borderBottom: "1px solid #f3f4f6",
        },
        removeButton: {
            background: "none",
            border: "none",
            color: "#ef4444",
            cursor: "pointer",
            fontSize: "0.8rem",
        },
        tableContainer: {
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "20px",
            marginTop: "20px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            overflowX: "auto",
            maxWidth: "100%",
        },
        tableTitle: {
            fontSize: "18px",
            fontWeight: "600",
            marginBottom: "20px",
            color: "#374151",
        },
        table: {
            width: "100%",
            borderCollapse: "collapse",
            marginBottom: "20px",
            minWidth: "800px",
        },
        tableHeader: {
            backgroundColor: "#f9fafb",
            borderBottom: "2px solid #e5e7eb",
        },
        th: {
            padding: "12px 8px",
            textAlign: "left",
            fontWeight: "600",
            color: "#374151",
            fontSize: "14px",
            border: "1px solid #e5e7eb",
            whiteSpace: "nowrap",
        },
        td: {
            padding: "12px 8px",
            borderBottom: "1px solid #e5e7eb",
            fontSize: "14px",
            color: "#6b7280",
            border: "1px solid #e5e7eb",
            verticalAlign: "top",
        },
        actionButton: {
            padding: "6px 12px",
            margin: "2px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "12px",
            cursor: "pointer",
            backgroundColor: "white",
        },
        viewButton: {
            color: "#3b82f6",
            borderColor: "#3b82f6",
        },
        deleteButton: {
            color: "#ef4444",
            borderColor: "#ef4444",
        },
        addToBasketButton: {
            backgroundColor: "#7fb3a3",
            color: "white",
            border: "none",
        },
        select: {
            padding: "6px 8px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
            backgroundColor: "white",
            minWidth: "120px",
            maxWidth: "140px",
        },
        input: {
            padding: "6px 8px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
            width: "100px",
            maxWidth: "120px",
        },
        // Modal styles
        modalOverlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
        },
        modalContent: {
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "30px",
            maxWidth: "600px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            position: "relative",
        },
        modalClose: {
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "none",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            color: "#6b7280",
        },
        uploadNewButton: {
            backgroundColor: "#7fb3a3",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "6px",
            fontSize: "16px",
            cursor: "pointer",
            marginTop: "50px",
            marginBottom: "20px",
            fontWeight: "600",
            display: "block",
        },
    }

    return (
        <Layout
            activeMenuItem="Upload Prayer Forms"
            breadcrumb={breadcrumb}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
        >
            {/* Toast notifications will be shown via react-toastify */}

            {/* Upload New Files Button */}
            <button
                style={styles.uploadNewButton}
                onClick={handleOpenUploadModal}
            >
                Upload New Files
            </button>

            {/* Uploaded Files Table - Always visible */}
            <div style={styles.tableContainer}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={styles.tableTitle}>Uploaded Forms</h2>
                    {uploadedFiles.length > 0 && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {uploadedFiles.length > 1 && (
                                <button
                                    style={{
                                        backgroundColor: processingFileId === 'bulk' ? '#d1d5db' : (selectAll ? '#6b7280' : '#7fb3a3'),
                                        color: processingFileId === 'bulk' ? '#6b7280' : 'white',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        cursor: processingFileId === 'bulk' ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                    }}
                                    onClick={() => processingFileId !== 'bulk' && handleSelectAll(!selectAll)}
                                    disabled={processingFileId === 'bulk'}
                                >
                                    {selectAll ? 'Deselect All' : 'Select All'}
                                </button>
                            )}
                            {uploadedFiles.length > 1 ? (
                                <button
                                    style={{
                                        backgroundColor: processingFileId === 'bulk' ? '#9ca3af' : (selectedFileIds.size > 0 || uploadedFiles.length > 0 ? '#7fb3a3' : '#d1d5db'),
                                        color: selectedFileIds.size > 0 || uploadedFiles.length > 0 || processingFileId === 'bulk' ? 'white' : '#6b7280',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        cursor: processingFileId === 'bulk' ? 'not-allowed' : (selectedFileIds.size > 0 || uploadedFiles.length > 0 ? 'pointer' : 'not-allowed'),
                                        fontSize: '14px',
                                        fontWeight: '600',
                                    }}
                                    onClick={handleAddToCartClick}
                                    disabled={processingFileId === 'bulk' || (selectedFileIds.size === 0 && uploadedFiles.length === 0)}
                                >
                                    {processingFileId === 'bulk'
                                        ? `Adding ${selectedFileIds.size > 0 ? selectedFileIds.size : uploadedFiles.length} items...`
                                        : `Add to Cart (${selectedFileIds.size > 0 ? selectedFileIds.size : uploadedFiles.length})`}
                                </button>
                            ) : null}
                        </div>
                    )}
                </div>
                {uploadedFiles.length === 0 ? (
                    <p>No uploaded files found. Click "Upload New Files" to get started.</p>
                ) : (
                    <table style={styles.table}>
                        <thead style={styles.tableHeader}>
                        <tr>
                            {uploadedFiles.length > 1 && (
                                <th style={{ ...styles.th, width: '50px' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                    />
                                </th>
                            )}
                            <th style={styles.th}>REF.NO</th>
                            <th style={styles.th}>REQUEST TYPE</th>
                            <th style={styles.th}>FORM</th>
                            <th style={styles.th}>AMOUNT</th>
                            <th style={styles.th}>REMARKS</th>
                            <th style={styles.th}>ACTION</th>
                        </tr>
                        </thead>
                        <tbody>
                        {uploadedFiles.map((file, index) => (
                            <tr key={file.id}>
                                {uploadedFiles.length > 1 && (
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedFileIds.has(file.id)}
                                            onChange={(e) => handleFileSelect(file.id, e.target.checked)}
                                            style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                        />
                                    </td>
                                )}
                                <td style={styles.td}>{index + 1}</td>
                                <td style={styles.td}>
                                    <select
                                        style={styles.select}
                                        value={formSubmissions[file.id]?.requestType || ""}
                                        onChange={(e) =>
                                            handleFormSubmissionChange(
                                                file.id,
                                                "requestType",
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">Select a form</option>
                                        {isLoadingForms ? (
                                            <option disabled>Loading forms...</option>
                                        ) : (
                                            forms.map((form) => (
                                                <option
                                                    key={form.id}
                                                    value={form.formType}
                                                    data-orientation={form.orientation}
                                                    data-report-id={form.report_id}
                                                >
                                                    {form.formType}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </td>
                                <td style={styles.td}>
                                    <button
                                        style={{
                                            ...styles.actionButton,
                                            ...styles.viewButton,
                                        }}
                                        onClick={() => handleViewFile(file.id)}
                                    >
                                        Preview
                                    </button>
                                </td>
                                <td style={styles.td}>
                                    <input
                                        type="number"
                                        placeholder="Enter the amount"
                                        style={styles.input}
                                        value={formSubmissions[file.id]?.amount || ""}
                                        onChange={(e) =>
                                            handleFormSubmissionChange(
                                                file.id,
                                                "amount",
                                                e.target.value
                                            )
                                        }
                                        step="0.01"
                                        min="0.01"
                                    />
                                </td>
                                <td style={styles.td}>
                                    <input
                                        type="text"
                                        placeholder="Enter your remarks"
                                        style={{ ...styles.input, width: "200px" }}
                                        value={formSubmissions[file.id]?.remarks || ""}
                                        onChange={(e) =>
                                            handleFormSubmissionChange(
                                                file.id,
                                                "remarks",
                                                e.target.value
                                            )
                                        }
                                    />
                                </td>
                                <td style={styles.td}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {uploadedFiles.length === 1 && (
                                            <button
                                                style={{
                                                    ...styles.actionButton,
                                                    ...styles.addToBasketButton,
                                                    padding: "8px 16px",
                                                    fontSize: "12px",
                                                    fontWeight: "600",
                                                    backgroundColor: processingFileId === file.id ? "#9ca3af" : "#7fb3a3",
                                                    cursor: processingFileId === file.id ? "not-allowed" : "pointer",
                                                }}
                                                onClick={() => handleAddToBasket(file)}
                                                disabled={processingFileId === file.id}
                                            >
                                                {processingFileId === file.id ? "ADDING..." : "ADD TO BASKET"}
                                            </button>
                                        )}
                                        <button
                                            style={{
                                                ...styles.actionButton,
                                                ...styles.deleteButton,
                                                padding: "8px 12px",
                                                fontSize: "14px",
                                                minWidth: "40px",
                                            }}
                                            onClick={() => handleDeleteFile(file.id)}
                                            title="Delete file"
                                        >
                                            🗑
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Upload Modal */}
            {showUploadModal && (
                <div style={styles.modalOverlay} onClick={() => setShowUploadModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button
                            style={styles.modalClose}
                            onClick={() => setShowUploadModal(false)}
                        >
                            ×
                        </button>

                        <h2 style={{ marginBottom: "20px", color: "#374151" }}>Upload Prayer Forms</h2>

                        <div
                            style={styles.uploadArea}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <p style={styles.uploadTitle}>
                                Please select prayer forms you would like to upload. You can
                                upload multiple forms at once.
                            </p>
                            <div style={styles.uploadIcon}>
                                <CloudUploadIcon />
                            </div>
                            <p style={styles.uploadText}>
                                Drop files here or click to upload.
                            </p>
                            <button
                                style={styles.selectButton}
                                onClick={handleSelectClick}
                                onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#4b5563")
                                }
                                onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#6b7280")
                                }
                            >
                                SELECT
                            </button>
                            <p style={styles.fileRestriction}>
                                (only PDF and JPG files are accepted)
                            </p>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg"
                            onChange={handleFileInputChange}
                            style={styles.hiddenInput}
                        />

                        {selectedFiles.length > 0 && (
                            <div style={styles.selectedFiles}>
                                <h3 style={{ marginBottom: "15px", color: "#374151" }}>
                                    Selected Files:
                                </h3>
                                {selectedFiles.map((file, index) => (
                                    <div key={index} style={styles.fileItem}>
                                        <span style={styles.fileName}>
                                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                        </span>
                                        <button
                                            style={styles.removeButton}
                                            onClick={() => {
                                                setSelectedFiles((prev) =>
                                                    prev.filter((_, i) => i !== index)
                                                );
                                                // Reset file input to ensure it works for future selections
                                                if (fileInputRef.current) {
                                                    fileInputRef.current.value = "";
                                                }
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                                <button
                                    style={{
                                        backgroundColor: isUploading ? "#9ca3af" : "#4b5563",
                                        color: "white",
                                        border: "none",
                                        padding: "12px 25px",
                                        borderRadius: "6px",
                                        cursor: isUploading ? "not-allowed" : "pointer",
                                        fontSize: "1rem",
                                        marginTop: "20px",
                                    }}
                                    onClick={handleUploadFiles}
                                    disabled={isUploading}
                                >
                                    {isUploading ? "Uploading..." : "Upload Files"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Bulk Add Confirmation Modal */}
            {showBulkAddModal && (
                <div style={styles.modalOverlay} onClick={() => setShowBulkAddModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button
                            style={styles.modalClose}
                            onClick={() => {
                                setShowBulkAddModal(false);
                                setBulkAddOption(null);
                            }}
                        >
                            ×
                        </button>

                        <h2 style={{ marginBottom: "20px", color: "#374151" }}>Add Items to Cart</h2>

                        <p style={{ marginBottom: "25px", fontSize: "16px", color: "#6b7280" }}>
                            You have {uploadedFiles.length} uploaded file(s). How would you like to proceed?
                        </p>

                        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                            <button
                                style={{
                                    backgroundColor: processingFileId === 'bulk' ? "#9ca3af" : "#7fb3a3",
                                    color: "white",
                                    border: "none",
                                    padding: "12px 24px",
                                    borderRadius: "6px",
                                    fontSize: "16px",
                                    cursor: processingFileId === 'bulk' ? "not-allowed" : "pointer",
                                    fontWeight: "600",
                                    transition: "background-color 0.2s",
                                }}
                                onClick={() => {
                                    if (processingFileId !== 'bulk') {
                                        setBulkAddOption('all');
                                        handleBulkAddToBasket();
                                    }
                                }}
                                disabled={processingFileId === 'bulk'}
                                onMouseEnter={(e) => processingFileId !== 'bulk' && (e.currentTarget.style.backgroundColor = "#6a9589")}
                                onMouseLeave={(e) => processingFileId !== 'bulk' && (e.currentTarget.style.backgroundColor = "#7fb3a3")}
                            >
                                {processingFileId === 'bulk' && bulkAddOption === 'all'
                                    ? `Adding All ${uploadedFiles.length} Items...`
                                    : `Add All ${uploadedFiles.length} Item(s) to Cart`}
                            </button>

                            {selectedFileIds.size > 0 && (
                                <button
                                    style={{
                                        backgroundColor: processingFileId === 'bulk' ? "#9ca3af" : "#6b7280",
                                        color: "white",
                                        border: "none",
                                        padding: "12px 24px",
                                        borderRadius: "6px",
                                        fontSize: "16px",
                                        cursor: processingFileId === 'bulk' ? "not-allowed" : "pointer",
                                        fontWeight: "600",
                                        transition: "background-color 0.2s",
                                    }}
                                    onClick={() => {
                                        if (processingFileId !== 'bulk') {
                                            setBulkAddOption('selected');
                                            handleBulkAddToBasket();
                                        }
                                    }}
                                    disabled={processingFileId === 'bulk'}
                                    onMouseEnter={(e) => processingFileId !== 'bulk' && (e.currentTarget.style.backgroundColor = "#4b5563")}
                                    onMouseLeave={(e) => processingFileId !== 'bulk' && (e.currentTarget.style.backgroundColor = "#6b7280")}
                                >
                                    {processingFileId === 'bulk' && bulkAddOption === 'selected'
                                        ? `Adding Selected ${selectedFileIds.size} Items...`
                                        : `Add Selected ${selectedFileIds.size} Item(s) to Cart`}
                                </button>
                            )}

                            <button
                                style={{
                                    backgroundColor: "white",
                                    color: "#6b7280",
                                    border: "1px solid #d1d5db",
                                    padding: "12px 24px",
                                    borderRadius: "6px",
                                    fontSize: "16px",
                                    cursor: processingFileId === 'bulk' ? "not-allowed" : "pointer",
                                    fontWeight: "600",
                                }}
                                onClick={() => {
                                    if (processingFileId !== 'bulk') {
                                        setShowBulkAddModal(false);
                                        setBulkAddOption(null);
                                    }
                                }}
                                disabled={processingFileId === 'bulk'}
                            >
                                Select Items First
                            </button>
                        </div>

                        <p style={{
                            marginTop: "20px",
                            fontSize: "14px",
                            color: "#9ca3af",
                            fontStyle: "italic"
                        }}>
                            Note: Please ensure all required fields (Request Type, Amount) are filled for each item.
                        </p>
                    </div>
                </div>
            )}

        </Layout>
    )
}

export default UploadPage